import type { Directive } from 'vue'

// Forms exposed to AI agents as WebMCP tools (declarative API):
// https://developer.chrome.com/docs/ai/webmcp/declarative-api
// The browser builds a tool from a form with toolname/tooldescription and describes each field
// by toolparamdescription, then by its <label>. Fields of the request forms are shared,
// so they are described in one place.
const PARAM_DESCRIPTIONS: Record<string, string> = {
  phone: 'Mobile phone number in Kyrgyzstan without the +996 code, 9 digits.',
  amount: 'Amount of money in the selected currency.',
  currency_id: 'Currency of the amount.',
  currency: 'Currency of the amount.',
  period: 'Term in months.',
  location_id: 'City or region where the applicant wants to be served.',
  branch_id: 'Bank branch in the selected city where the applicant wants to be served.',
  product_id: 'Bank product the application is for.',
  credit_purpose_id: 'Purpose of the credit.',
  question_category_id: 'Topic of the question.'
}

export interface IWebMcpTool {
  name: string
  description: string
  // Submit the form when the agent calls the tool; only for forms without side effects
  autosubmit?: boolean
}

const OBSERVERS = new WeakMap<HTMLFormElement, MutationObserver>()

const describeFields = (form: HTMLFormElement) => {
  for (const field of form.querySelectorAll<HTMLElement>('[name]')) {
    const description = PARAM_DESCRIPTIONS[field.getAttribute('name') ?? '']
    if (description && !field.hasAttribute('toolparamdescription')) {
      field.setAttribute('toolparamdescription', description)
    }
  }
}

/**
 * Turns a form into a WebMCP tool: v-webmcp-tool="{ name, description }".
 *
 * The browser validates the tool schema on every change of a tool attribute and keeps the
 * warnings, so the form must get all of them at once and only after its fields are described
 * (select components render their native <select> without a label, so the server markup
 * cannot carry the descriptions). The form is taken out of the document while the attributes
 * are set and put back in the same place: Vue keeps the same element. Fields added later
 * (a branch after a city) are described as they appear.
 */
export const vWebmcpTool: Directive<HTMLFormElement, IWebMcpTool> = {
  getSSRProps: () => ({}),
  mounted(form, { value }) {
    describeFields(form)

    const parent = form.parentNode
    const next = form.nextSibling
    form.remove()
    form.setAttribute('toolname', value.name)
    form.setAttribute('tooldescription', value.description)
    form.toggleAttribute('toolautosubmit', Boolean(value.autosubmit))
    parent?.insertBefore(form, next)

    const observer = new MutationObserver(() => describeFields(form))
    observer.observe(form, { childList: true, subtree: true })
    OBSERVERS.set(form, observer)
  },
  unmounted(form) {
    OBSERVERS.get(form)?.disconnect()
    OBSERVERS.delete(form)
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('webmcp-tool', vWebmcpTool)
})
